import { APIKit } from "config/axios/index";
import store from "config/redux/index";
import { start, end } from "config/redux/slice";

import { ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import { Bank, CoverLetterLanguage, DocLanguage } from "types";

import { AppUtils, FileUtils } from "utils";

import { handleErrorWithTokenRenewal } from "./common";

const { API_V1_CONTRACT, API_V1_CONTRACT_ATTACHMENTS } = ServerConstants;

const {
  REQUEST_CONTRACT_GET_ID,
  REQUEST_CONTRACT_OPTIONS_ID,
  REQUEST_CONTRACT_UPDATE_MULTIPLE_ID,
  REQUEST_CONTRACT_ATTACHMENT_LIST_ID,
  REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID,
  REQUEST_CONTRACT_ATTACHMENT_DELETE_ID,
} = ServiceConstants;

export async function getContract(bankCreditRequestId: number, bank: Bank) {
  const requestId = REQUEST_CONTRACT_GET_ID + "_" + bank;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CONTRACT}/${bankCreditRequestId}/${bank}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, getContract, [bankCreditRequestId, bank], requestId);
  }
}

export async function getContractOptions() {
  const requestId = REQUEST_CONTRACT_OPTIONS_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CONTRACT}/options`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, getContractOptions, [], requestId);
  }
}

export async function contractUpdateMultipleProperties(contractId: number, data: Record<string, unknown>) {
  const requestId = REQUEST_CONTRACT_UPDATE_MULTIPLE_ID;
  try {
    store.dispatch(start(requestId));
    const body: { name: string; value: unknown }[] = [];
    Object.keys(data).forEach((key) => {
      body.push({ name: key, value: AppUtils.convertToAppropriateValue(data[key]) });
    });
    const response = await APIKit.patch(`${API_V1_CONTRACT}/${contractId}/multiple`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, contractUpdateMultipleProperties, [contractId, data], requestId);
  }
}

export async function downloadCoverLetter(
  contractId: number,
  lang: CoverLetterLanguage = "DE",
  clientFullName?: string,
) {
  const response = await APIKit.get(`${API_V1_CONTRACT}/${contractId}/cover-letter`, {
    params: { lang },
    responseType: "blob",
  });

  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = FileUtils.buildClientDocumentFileName(clientFullName, "pdf", "cover-letter");
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function downloadBetreuungskostenformular(
  contractId: number,
  lang: DocLanguage = "DE",
  clientFullName?: string,
) {
  const response = await APIKit.get(`${API_V1_CONTRACT}/${contractId}/betreuungskostenformular`, {
    responseType: "blob",
    headers: {
      ...APIKit.defaults.headers["common"],
      "Accept-Language": lang,
    },
  });

  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = FileUtils.buildClientDocumentFileName(
    clientFullName,
    "pdf",
    "betreuungskostenformular",
  );
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function getContractAttachments(contractId: number) {
  const requestId = REQUEST_CONTRACT_ATTACHMENT_LIST_ID + "_" + contractId;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(API_V1_CONTRACT_ATTACHMENTS, {
      params: { contractId },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, getContractAttachments, [contractId], requestId);
  }
}

export async function uploadUnsignedContract(bankCreditRequestId: number, bank: Bank, file: File) {
  const requestId = REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID + "_UNSIGNED_" + bank;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(
      `${API_V1_CONTRACT_ATTACHMENTS}/unsigned-contract`,
      formData,
      {
        params: { bankCreditRequestId, bank },
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, uploadUnsignedContract, [bankCreditRequestId, bank, file], requestId);
  }
}

export async function deleteContractAttachment(attachmentId: string) {
  const requestId = REQUEST_CONTRACT_ATTACHMENT_DELETE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_CONTRACT_ATTACHMENTS}/${attachmentId}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, deleteContractAttachment, [attachmentId], requestId);
  }
}

export async function downloadContractAttachment(attachmentId: string, fileName?: string, extension?: string) {
  const response = await APIKit.get(`${API_V1_CONTRACT_ATTACHMENTS}/${attachmentId}`, {
    responseType: "blob",
  });

  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName ? `${fileName}${extension ? `.${extension}` : ""}` : attachmentId;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function uploadOtherAttachment(contractId: number, file: File) {
  const requestId = REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID + "_OTHER";
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(
      `${API_V1_CONTRACT_ATTACHMENTS}/other`,
      formData,
      {
        params: { contractId },
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, uploadOtherAttachment, [contractId, file], requestId);
  }
}

export async function uploadSignedContract(contractId: number, file: File) {
  const requestId = REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID + "_SIGNED";
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append("file", file);
    const response = await APIKit.post(
      `${API_V1_CONTRACT_ATTACHMENTS}/signed-contract`,
      formData,
      {
        params: { contractId },
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, uploadSignedContract, [contractId, file], requestId);
  }
}
